import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { MarkupKindSchema } from './markups.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Markup profiles (Pricing → Markup Profiles). The quote-engine markup master
 * ported from legacy `tbl_MarkupMaster` (see docs/migration/markup-system-plan.md).
 *
 * A profile is either the single GLOBAL default or one per destination city.
 * Each carries per-category MarkupRule rows auto-applied during itinerary quote
 * calculation. This is distinct from the generic `Markup` catalog (markups.ts).
 */

/** Mirrors Prisma `MarkupScope`. */
export const MarkupScopeSchema = z.enum(['GLOBAL', 'DESTINATION']);
export type MarkupScope = z.infer<typeof MarkupScopeSchema>;

/** Mirrors Prisma `MarkupCategory`. TBO_* are supplier-sourced (source=TBO). */
export const MarkupCategorySchema = z.enum([
  'TBO_HOTEL',
  'TBO_FLIGHT',
  'HOTEL',
  'ACTIVITY',
  'TRANSFER',
  'VAS',
  'OVERALL',
]);
export type MarkupCategory = z.infer<typeof MarkupCategorySchema>;

/** Mirrors Prisma `MarkupAppliedTo`. Only meaningful for in-house categories. */
export const MarkupAppliedToSchema = z.enum(['GLOBAL', 'FIELD', 'MIXED']);
export type MarkupAppliedTo = z.infer<typeof MarkupAppliedToSchema>;

export const MarkupRuleSchema = z.object({
  category: MarkupCategorySchema,
  kind: MarkupKindSchema,
  /** PERCENT: 12.5 => 12.5%. FLAT: raw amount per component in itinerary currency. */
  value: z.number(),
  appliedTo: MarkupAppliedToSchema,
});
export type MarkupRule = z.infer<typeof MarkupRuleSchema>;

export const MarkupProfileListItemSchema = z.object({
  id: UuidSchema,
  scope: MarkupScopeSchema,
  cityId: UuidSchema.nullable(),
  cityName: z.string().nullable(),
  name: z.string(),
  isActive: z.boolean(),
  ruleCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type MarkupProfileListItem = z.infer<typeof MarkupProfileListItemSchema>;

export const MarkupProfileListQuerySchema = PaginationQuerySchema.extend({
  scope: MarkupScopeSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type MarkupProfileListQuery = z.infer<typeof MarkupProfileListQuerySchema>;

export const MarkupProfileListResponseSchema = paginatedResponseSchema(MarkupProfileListItemSchema);
export type MarkupProfileListResponse = z.infer<typeof MarkupProfileListResponseSchema>;

export const MarkupProfileDetailSchema = MarkupProfileListItemSchema.extend({
  rules: z.array(MarkupRuleSchema),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type MarkupProfileDetail = z.infer<typeof MarkupProfileDetailSchema>;

const MarkupProfileCoreFieldsSchema = z
  .object({
    scope: MarkupScopeSchema,
    /** Required when scope=DESTINATION; ignored/null for GLOBAL. */
    cityId: UuidSchema.nullable().optional(),
    name: z.string().trim().min(1, 'Name is required').max(160),
    isActive: z.boolean().optional(),
    /** Full rule set (replace-all on update). Categories must be unique. */
    rules: z.array(MarkupRuleSchema).max(7),
  })
  .refine((v) => v.scope !== 'DESTINATION' || !!v.cityId, {
    message: 'A destination profile requires a city',
    path: ['cityId'],
  })
  .refine((v) => new Set(v.rules.map((r) => r.category)).size === v.rules.length, {
    message: 'Duplicate category in rules',
    path: ['rules'],
  })
  .refine((v) => v.rules.every((r) => r.kind !== 'PERCENT' || (r.value >= 0 && r.value <= 100)), {
    message: 'A percent markup must be between 0 and 100',
    path: ['rules'],
  });

export const CreateMarkupProfileRequestSchema = MarkupProfileCoreFieldsSchema;
export type CreateMarkupProfileRequest = z.infer<typeof CreateMarkupProfileRequestSchema>;

export const UpdateMarkupProfileRequestSchema = MarkupProfileCoreFieldsSchema;
export type UpdateMarkupProfileRequest = z.infer<typeof UpdateMarkupProfileRequestSchema>;

/**
 * Auto-computed markup suggestion for an itinerary. Category keys match the
 * builder's PricingStep buckets (HOTEL/FLIGHT/ACTIVITY/TRANSFER/ADDON). All
 * amounts are paise strings. The builder pre-fills the PricingStep from this
 * and the agent may override before saving.
 */
export const MarkupSuggestionSchema = z.object({
  /**
   * Per-category product markup INCLUDING each bucket's share of the OVERALL
   * top-up (kept for backwards compatibility with published snapshots that
   * were saved this way). The UI subtracts `overallMarkupPaise` proportionally
   * so it can render OVERALL as its own row and category rows show the raw
   * per-category rate.
   */
  markupByCategory: z.record(z.string(), z.number()),
  netMarkupPaise: z.string(),
  gstPaise: z.string(),
  /**
   * Total OVERALL top-up baked into `markupByCategory`. Zero when no active
   * OVERALL rule exists. Lets the client separate product-category markup
   * from the OVERALL contribution in the pricing UI.
   */
  overallMarkupPaise: z.string().default('0'),
  perComponent: z.array(z.object({ componentId: UuidSchema, markupPaise: z.string() })),
  /** Which profile drove each component's city (debug/traceability). */
  resolvedGlobalOnly: z.boolean(),
});
export type MarkupSuggestion = z.infer<typeof MarkupSuggestionSchema>;
